import React from "react";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Film, BarChart2, DollarSign, CheckSquare, TrendingUp } from "react-feather";
import VideoRightsTopBar from "./video/VideoRightsTopBar";
import { tt } from "../i18n/tt";
import LayWidget from "../components/lay/LayWidget";
import { lazyI18n } from "../i18n/lazyText";
import HomeCardStack from "../components/homeCard/HomeCardStack";
import { useUser } from "../contexts/UserContext";
import { RightsTodoProvider, useRightsTodo } from "../contexts/RightsTodoContext";
import { todoBadgeText, RIGHTS_TODO_PATH } from "../lib/rightsTodo";

const FILLABLE_ICONS = new Set([Film]);
// 본문을 1200px 보다 넓게 쓰는 화면(PC). 아래 Box maxW 참고.
const WIDE_PATHS = ["/video/inspection-manage"];

const BOTTOM_NAV_ITEMS = lazyI18n(() => [
  { label: tt("a.9190db22", "작품"), path: "/video/rights-manager", icon: Film },
  // 2026-10-21 「검수」 → 「검수/화리」(상단바와 같은 이름 · 할 일 숫자 배지)
  { label: tt("a.todo.nav", "검수/화리"), path: "/video/inspection-manage", icon: CheckSquare, todo: true },
  { label: tt("a.94f0d012", "수익"), path: "/video-dashboard", icon: BarChart2 },
  { label: tt("a.50f3392c", "인사이트"), path: "/video-insight", icon: TrendingUp },
  { label: tt("a.15d47b30", "정산"), path: "/total-copyrights", icon: DollarSign },
]);

// 할 일 숫자(상단바·하단 탭·작품 탭 카드·검수/화리 탭이 같이 본다) — RightsTodoProvider 가 이 레이아웃 전체를 감싼다
const VideoRightsLayout = () => (
  <RightsTodoProvider>
    <VideoRightsLayoutInner />
  </RightsTodoProvider>
);

const VideoRightsLayoutInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const { todo } = useRightsTodo();
  const todoTotal = todo?.total || 0;

  return (
    <Flex direction="column" minH="100vh" bg="neutral.50">
      <VideoRightsTopBar />
      <Box
        flex={1}
        p={{ base: 4, md: 8 }}
        pb={{ base: "80px", md: 8 }}
        minH="calc(100vh - 56px)"
        // 2026-10-16(제이 요청 2026-10-14) — 검수 관리는 왼쪽 목록 + 검수 행(영상·점검·화리·처리 버튼)이 한 줄에 들어가야
        // 한 화면에 많이 보인다. 이 화면만 넓게 쓰고, 나머지 권리사 화면은 1200px 그대로.
        maxW={{ base: "100%", md: WIDE_PATHS.includes(location.pathname) ? "1680px" : "1200px" }}
        w="100%"
        mx={{ base: 0, md: "auto" }}
      >
        {/* 홈 상단 카드(2026-10-19 · 알림 관리 › 카드 발송) — 권리사 첫 화면(작품)에서만. 없으면 안 그림 */}
        {location.pathname === "/video/rights-manager" && <HomeCardStack email={userInfo?.email} />}
        <Outlet />
        {/* 레이 AI 도우미 — 권리사 포털에도 같은 위젯(범위는 서버가 role 로 판정) */}
        {process.env.REACT_APP_LAY_WIDGET !== "0" && <LayWidget />}
        {/* Footer */}
        <Box
          color="neutral.400"
          fontSize="xs"
          mt={10}
          mb={2}
          textAlign="center"
        >
          <Text mb={1} lineHeight="1.8">
            {tt("a.footer.company", "(주) 루나르트 | 대표 권재의")}<br />
            {tt("a.footer.address", "주소 서울특별시 성동구 연무장5가길 25, 8층 804호(성수동2가, 성수역 SKV1 Tower) (주)루나르트")}<br />
            {tt("a.footer.bizno", "사업자등록번호 652-88-00793")}<br />
            {tt("a.footer.mailorder", "통신판매업 신고 2024-서울광진-0001")}<br />
            {tt("a.footer.contact", "고객 문의 contact@laeebly.com")}<br />
            {tt("a.footer.hosting", "호스팅 서비스 Amazon Web Services · 개인정보 보호책임자 권재의")}<br />
            Believe in yourself © Rhoonart Co., Ltd. All Rights Reserved.
          </Text>
          <Flex gap={2} justify="center">
            <Button
              variant="link"
              color="neutral.400"
              fontSize="xs"
              fontWeight="normal"
              p={0}
              h="auto"
              minH="auto"
              onClick={() => navigate('/terms-of-service')}
              _hover={{ color: "neutral.600" }}
            >
              {tt("a.2aa88b28", "이용약관")}
            </Button>
            <Text color="neutral.300">|</Text>
            <Button
              variant="link"
              color="neutral.400"
              fontSize="xs"
              fontWeight="normal"
              p={0}
              h="auto"
              minH="auto"
              onClick={() => navigate('/privacy-policy')}
              _hover={{ color: "neutral.600" }}
            >
              {tt("a.5381fd49", "개인정보처리방침")}
            </Button>
          </Flex>
        </Box>
      </Box>

      {/* Bottom Nav Bar — mobile only */}
      <Box
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        zIndex={100}
        w="100%"
        display={{ base: "block", md: "none" }}
        pb="env(safe-area-inset-bottom)"
        bg="rgba(255,255,255,0.85)"
        sx={{ backdropFilter: "saturate(180%) blur(20px)" }}
        borderTop="0.5px solid"
        borderColor="neutral.200"
      >
        <Flex
          w="100%"
          py="6px"
          px="2px"
          justify="space-around"
          align="center"
        >
          {BOTTOM_NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            const IconComponent = item.icon;
            return (
              <Flex
                key={item.path}
                flex={1}
                direction="column"
                align="center"
                justify="center"
                gap="2px"
                py="4px"
                cursor="pointer"
                onClick={() => navigate(item.todo && todoTotal > 0 ? RIGHTS_TODO_PATH : item.path)}
                transition="all 0.2s ease"
                _active={{ transform: "scale(0.9)" }}
                role="group"
              >
                <Box
                  color={isActive ? "brand.600" : "neutral.400"}
                  transition="color 0.2s ease"
                  position="relative"
                >
                  {item.todo && todoBadgeText(todoTotal) && (
                    <Box
                      as="span"
                      position="absolute"
                      top="-6px"
                      left="14px"
                      minW="18px"
                      h="18px"
                      px="5px"
                      borderRadius="full"
                      bg="status.error"
                      color="white"
                      fontSize="11px"
                      fontWeight="800"
                      lineHeight="18px"
                      textAlign="center"
                      data-rights-todo-count={todoTotal}
                    >
                      {todoBadgeText(todoTotal)}
                    </Box>
                  )}
                  <IconComponent
                    size={22}
                    stroke={isActive && FILLABLE_ICONS.has(IconComponent) ? "white" : "currentColor"}
                    strokeWidth={isActive ? (FILLABLE_ICONS.has(IconComponent) ? 1.6 : 2.2) : 1.4}
                    fill={isActive && FILLABLE_ICONS.has(IconComponent) ? "currentColor" : "none"}
                  />
                </Box>
                <Text
                  fontSize="12px"
                  lineHeight="1"
                  fontWeight={isActive ? "600" : "400"}
                  color={isActive ? "brand.600" : "neutral.400"}
                  transition="all 0.2s ease"
                >
                  {item.label}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </Flex>
  );
};

export default VideoRightsLayout;
